import { Link } from "react-router-dom";
import React from "react";
import PropTypes from "prop-types";
import { Table as MuiTable } from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import SoftBox from "components/SoftBox";
import SoftAvatar from "components/SoftAvatar";
import SoftTypography from "components/SoftTypography";
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";
import {
  DndContext,
  DragOverlay,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  DragHandle,
  FullRowOverlay,
  dropAnimation,
  sortAutoScroll,
  useVerticalSortSensors,
  verticalSortModifiers,
} from "./dragDrop";

function buildCells({
  columns,
  row,
  key,
  rows,
  whitebackground,
}) {
  const isEvenRow = key % 2 === 0;
  const changeColor = row.changeColor || false;

  return columns.map(({ name, align }) => {
    if (Array.isArray(row[name])) {
      return (
        <SoftBox
          key={`${name}-${key}`}
          component="td"
          p={1}
          sx={{
            backgroundColor: isEvenRow ? "white" : "#F1F4F8",
            border: "none",
            verticalAlign: "middle",
          }}
        >
          <SoftBox display="flex" alignItems="center" py={0.5} px={1}>
            <SoftBox mr={2}>
              <SoftAvatar
                src={row[name][0]}
                name={row[name][1]}
                variant="rounded"
                size="sm"
              />
            </SoftBox>
            <SoftTypography
              variant="button"
              fontWeight="medium"
              sx={{ width: "max-content" }}
            >
              {row[name][1]}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      );
    }

    return (
      <SoftBox
        key={`${name}-${key}`}
        component="td"
        px={1}
        py={1.5}
        textAlign={align}
        sx={{
          backgroundColor: changeColor
            ? "#F8FE01"
            : whitebackground
            ? "white"
            : isEvenRow
            ? "white"
            : "#F1F4F8",
          borderBottom:
            whitebackground && key !== rows?.length - 1
              ? "0.5px solid #000000 !important"
              : "",
          verticalAlign: whitebackground ? "top" : "middle",
        }}
      >
        <SoftTypography
          variant="button"
          fontWeight="regular"
          color="secondary"
          sx={{ display: "inline-block", width: "max-content" }}
        >
          {row[name]}
        </SoftTypography>
      </SoftBox>
    );
  });
}

function SortableTableRow({
  id,
  columns,
  row,
  rowIndex,
  rows,
  whitebackground,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const cells = buildCells({
    columns,
    row,
    key: rowIndex,
    rows,
    whitebackground,
  }).map((cell, index) => {
    if (columns[index].name !== " ") {
      return cell;
    }
    return (
      <SoftBox
        key={`drag-${id}`}
        component="td"
        px={1}
        py={1.5}
        textAlign="center"
        sx={{
          backgroundColor: rowIndex % 2 === 0 ? "white" : "#F1F4F8",
          verticalAlign: "middle",
        }}
      >
        <DragHandle attributes={attributes} listeners={listeners} />
      </SoftBox>
    );
  });

  return (
    <TableRow
      ref={setNodeRef}
      sx={{
        transform: isDragging ? "none" : CSS.Translate.toString(transform),
        transition: isDragging ? "none" : transition,
        opacity: isDragging ? 0 : 1,
      }}
    >
      {cells}
    </TableRow>
  );
}

function Table({
  columns,
  rows,
  radius,
  whitebackground,
  stickyTop = 0,
  stickyZIndex = 1,
  stickyHeader = true,
  onReorder,
  getRowId,
}) {
  const { lightBlack, shadow } = colors;
  const { size, fontWeightBold } = typography;
  const sensors = useVerticalSortSensors();
  const sortable = typeof onReorder === "function" && typeof getRowId === "function";
  const sortableIds = sortable ? rows.map((row) => String(getRowId(row))) : [];
  const [activeId, setActiveId] = React.useState(null);
  const [overlaySize, setOverlaySize] = React.useState({ width: 0, height: 0 });
  const activeRow = sortable
    ? rows.find((row) => String(getRowId(row)) === String(activeId))
    : null;

  const renderColumns = columns.map(({ name, align, width }, key) => {
    let pl;
    let pr;

    if (key === 0) {
      pl = 3;
      pr = 3;
    } else if (key === columns.length - 1) {
      pl = 3;
      pr = 3;
    } else {
      pl = 1;
      pr = 1;
    }

    return (
      <SoftBox
        key={name}
        component="th"
        width={width || "auto"}
        pt={1.75}
        pb={1.75}
        pl={align === "left" ? pl : 3}
        pr={align === "right" ? pr : 3}
        textAlign={align}
        fontSize={size.lg}
        fontWeight={fontWeightBold}
        color={lightBlack.main}
        opacity={1}
        backgroundColor={shadow.main}
        borderBottom="none !important"
        sx={{ borderRadius: "0 !important" }}
      >
        {name}
      </SoftBox>
    );
  });

  const handleDragStart = ({ active }) => {
    setActiveId(String(active.id));
    const rect = active.rect.current.initial;
    setOverlaySize({
      width: rect?.width || 0,
      height: rect?.height || 0,
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = sortableIds.indexOf(String(active.id));
    const newIndex = sortableIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    onReorder(arrayMove(sortableIds, oldIndex, newIndex));
  };

  const renderRows = rows.map((row, key) => {
    const rowKey = `row-${key}`;
    const linkPath = row.linkPath;
    const noLinkCells = row.noLinkCells || [];
    const tableRow = buildCells({
      columns,
      row,
      key,
      rows,
      whitebackground,
    });

    return (
      <TableRow key={rowKey}>
        {linkPath ? (
          <>
            {tableRow.map((cell, index) => (
              <React.Fragment key={index}>
                {noLinkCells.includes(columns[index].name) ? (
                  cell
                ) : (
                  <Link
                    to={linkPath}
                    style={{
                      textDecoration: "none",
                      color: "inherit",
                      display: "contents",
                    }}
                  >
                    {cell}
                  </Link>
                )}
              </React.Fragment>
            ))}
          </>
        ) : (
          tableRow
        )}
      </TableRow>
    );
  });

  const tableInner = (
    <MuiTable sx={{ borderRadius: 0, overflow: "hidden" }}>
      <SoftBox
        component="thead"
        sx={{
          borderRadius: 0,
          backgroundColor: "white",
          position: stickyHeader ? "sticky" : "static",
          top: stickyHeader ? stickyTop : "auto",
          zIndex: stickyHeader ? stickyZIndex : "auto",
        }}
      >
        <TableRow
          sx={{
            borderRadius: "0 !important",
            border: "none",
            "& > *": {
              border: "none !important",
            },
          }}
        >
          {renderColumns}
        </TableRow>
      </SoftBox>
      {sortable ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={verticalSortModifiers}
          autoScroll={sortAutoScroll}
          onDragStart={handleDragStart}
          onDragCancel={() => setActiveId(null)}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            <TableBody
              sx={{
                "& tr": {
                  border: "none ",
                  "& > *": {
                    border: "none ",
                  },
                },
                "& td, & th": {
                  border: "none ",
                },
              }}
            >
              {rows.map((row, key) => (
                <SortableTableRow
                  key={String(getRowId(row))}
                  id={String(getRowId(row))}
                  columns={columns}
                  row={row}
                  rowIndex={key}
                  rows={rows}
                  whitebackground={whitebackground}
                />
              ))}
            </TableBody>
          </SortableContext>
          <DragOverlay dropAnimation={dropAnimation} adjustScale={false} zIndex={20}>
            {activeRow ? (
              <FullRowOverlay
                width={overlaySize.width}
                height={overlaySize.height}
                backgroundColor={
                  rows.indexOf(activeRow) % 2 === 0 ? "white" : "#F1F4F8"
                }
              >
                {buildCells({
                  columns,
                  row: activeRow,
                  key: rows.indexOf(activeRow),
                  rows,
                  whitebackground,
                }).map((cell, index) => {
                  if (columns[index].name !== " ") {
                    return cell;
                  }
                  return (
                    <SoftBox
                      key="drag-overlay"
                      component="td"
                      px={1}
                      py={1.5}
                      textAlign="center"
                      sx={{
                        backgroundColor:
                          rows.indexOf(activeRow) % 2 === 0 ? "white" : "#F1F4F8",
                        verticalAlign: "middle",
                      }}
                    >
                      <DragHandle />
                    </SoftBox>
                  );
                })}
              </FullRowOverlay>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        <TableBody
          sx={{
            "& tr": {
              border: "none ",
              "& > *": {
                border: "none ",
              },
            },
            "& td, & th": {
              border: "none ",
            },
          }}
        >
          {renderRows}
        </TableBody>
      )}
    </MuiTable>
  );

  return (
    <TableContainer
      sx={{
        borderTopLeftRadius: radius ? "8px !important" : "",
        borderTopRightRadius: radius ? "8px !important" : "",
        borderRadius: radius ? "" : 0,
        overflowX: "hidden",
        overflowY: "visible",
        overscrollBehavior: "contain",
      }}
    >
      {tableInner}
    </TableContainer>
  );
}

Table.defaultProps = {
  columns: [],
  rows: [{}],
};

Table.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
};

export default Table;
